import React, { useState, useEffect } from 'react';
import { Box, Divider, Grid, Stack } from '@mui/material';
import { Controller, useWatch } from 'react-hook-form';
import { MyButton, MyTypography } from '../../../common/components';
import {
  initialValues as createProviderInitialValues,
} from '../CreateProvider/CreateProviderForm';
import CreateProviderForm from '../CreateProvider/CreateProviderForm';
import LabeledSwitch from '../../../common/components/LabeledSwitch';

export const DEFAULT_PROVIDER_NAME = 'Any/First Available';

export default function ProviderStep({
  control,
  errors,
  getValues,
  setValue,
  trigger,
  append,
  remove,
  fields,
  fieldsUser,
  appendUser,
  removeUser,
  watch,
}) {
  const watchedCanReceive = useWatch({
    control,
    name: 'providers.0.can_receive',
    defaultValue: false,
  });

  const clinicCanReceive = useWatch({
    control,
    name: 'clinic.can_receive',
    defaultValue: false,
  });

  const [showDefaultProvider, setShowDefaultProvider] = useState(watchedCanReceive);

  // Always append default provider (index 0) once on mount
  useEffect(() => {
    const defaultExists = getValues('providers')?.[0]?.is_default;
    if (!defaultExists) {
      append({
        ...createProviderInitialValues,
        firstname: DEFAULT_PROVIDER_NAME,
        lastname: '',
        title: '',
        can_receive: false,
        is_default: true,
      });
    }
  }, [append, getValues]);

  useEffect(() => {
    setShowDefaultProvider(watchedCanReceive);
  }, [watchedCanReceive]);

  return (
    <Grid item xs={12}>
      <Stack spacing={3}>

        <Controller
          name="clinic.can_receive"
          control={control}
          render={({ field }) => (
            <LabeledSwitch
              header="Can clinic receive referrals?"
              withSwitchLabel={true}
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
            />
          )}
        />

        {clinicCanReceive &&
          <Controller
            name="clinic.is_order"
            control={control}
            render={({ field }) => (
              <LabeledSwitch
                header="Referral Type"
                subheader="Does this clinic only receive orders?"
                withSwitchLabel={true}
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
        }

        <MyTypography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
          Providers
        </MyTypography>

        Note: Every Clinic has a default '{DEFAULT_PROVIDER_NAME}' provider

        {clinicCanReceive &&
          <LabeledSwitch
            subheader="Clinic receives to the default provider?"
            withSwitchLabel={true}
            checked={showDefaultProvider}
            onChange={(e) => {
              const checked = e.target.checked;
              setShowDefaultProvider(checked);

              const fieldPrefix = 'providers.0';
              setValue(`${fieldPrefix}.can_receive`, checked);
              trigger(fieldPrefix);
            }}
          />
        }

        {showDefaultProvider && (
          <Box>
            <MyTypography variant="h6" sx={{ mb: 2 }}>
              Default Provider Information
            </MyTypography>

            <CreateProviderForm
              key="default-provider"
              name="providers.0"
              prefix="providers.0"
              control={control}
              errors={errors}
              setValue={setValue}
              trigger={trigger}
              getValues={getValues}
              isDefault={true}
              watch={watch}
            />

            <Divider sx={{ my: 3 }} />
          </Box>
        )}

        <MyButton
          type="button"
          variant="contained"
          onClick={() => append({ ...createProviderInitialValues })}
          fullWidth={false}
          sx={{ width: 160 }}
        >
          Add Provider
        </MyButton>

        <Divider sx={{ my: 2 }} />

        {fields
          .filter((_item, index) => index > 0)
          .map((_item, index) => {
            const actualIndex = index + 1; // Adjust for skipping index 0
            return (
              <Box
                key={`box-providers-${actualIndex}`}
                sx={{
                  mb: 4,
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <MyTypography variant="h5">
                    Provider Information - {actualIndex}
                  </MyTypography>
                  <Box sx={{ flexGrow: 1 }} />
                  <MyButton
                    type="button"
                    variant="contained"
                    onClick={() => {
                      remove(actualIndex);

                      fieldsUser.forEach((u, idx) => {
                        if (u.providerIndex === actualIndex) {
                          removeUser(idx);
                        }
                      });
                    }}
                    fullWidth={false}
                  >
                    Remove
                  </MyButton>
                </Stack>

                <CreateProviderForm
                  key={`providers.${actualIndex}`}
                  name={`providers.${actualIndex}`}
                  prefix={`providers.${actualIndex}`}
                  control={control}
                  errors={errors}
                  setValue={setValue}
                  trigger={trigger}
                  getValues={getValues}
                  clinicCanReceive={clinicCanReceive}
                  fieldsUser={fieldsUser}
                  appendUser={appendUser}
                  providerIndex={actualIndex}
                  watch={watch}
                />
              </Box>
            );
          })}
      </Stack>
    </Grid>
  );
}
